import { useRouter } from 'next/router';
import { ChangeEvent, useRef, useState, useMemo } from 'react';
import { debounce } from 'lodash';
import { useOnClickOutside } from '../../hooks';
import { MarktechModal } from '../Atoms/Modal';
import { AutoComplete } from '../Common/AutoComplete';
import { BackIcon } from '../Common/CommonIcons';
import SearchInput from '../Common/SearchInput';
import { Input } from '../Common/SearchInput/styles';
import { CloseIcon } from '../Icons';
import { urlformatter } from '../Utils/CommonUtils';
import IFSCDataService from './Services/ifsc.service';
import { MobileSearchContainer, RootAutocomplete } from './styles';
import { SUGGESTION_LIST_TYPE } from './types';
import { getSuggestionList } from './utils';
import ReCAPTCHA from 'react-google-recaptcha';
import { RECAPTCHA_SITE_KEY } from '../Common/ContactSalesForm/constants';
import { getRecaptchaToken } from '../../utils/smart-filing/recaptcha.utils';

const SearchIFSC = (): JSX.Element => {
  const [openFilter, setOpenFilter] = useState(false);
  const onCloseModal = () => setOpenFilter(false);
  const onOpenModal = () => setOpenFilter(true);
  const [searchObj, updateSearchObj] = useState([]);
  const [isComponentVisible, setIsComponentVisible] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const [searchText, setSearchText] = useState('');
  const refAutocomplete = useRef(null);

  const router = useRouter();

  const updateSearch = async (text: string) => {
    try {
      const token = await getRecaptchaToken(recaptchaRef);
      if (token) {
        const response = await IFSCDataService.create(text, token);
        const data = response.data.results['0'].hits;
        const suggestionList = getSuggestionList(data) as never;
        updateSearchObj(suggestionList);
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      console.error('Error generating token :', errorMessage);
    }
  };

  const debouncedUpdateSearch = useMemo(() => debounce(updateSearch, 300), []);

  const onTextChanged = (e: ChangeEvent<HTMLInputElement>) => {
    const value: string = e.target.value;
    setIsComponentVisible(true);
    setSearchText(value);
    debouncedUpdateSearch(value);
  };

  const closeIconCB = () => {
    setSearchText('');
    setIsComponentVisible(false);
  };

  useOnClickOutside(refAutocomplete, () => {
    setSearchText('');
  });

  const routeToDetails = (item: SUGGESTION_LIST_TYPE) => {
    setIsComponentVisible(false);
    setSearchText('');
    updateSearchObj([]);
    onCloseModal();
    if (
      item.bank_name.label &&
      item.city &&
      item.branch_name &&
      item.ifsc_code
    ) {
      router.push(
        `/ifsc-code/${urlformatter(item.bank_name?.value)}/${urlformatter(
          item.city?.value
        )}/${urlformatter(item.branch_name?.value)}/${item.ifsc_code.value}/`
      );
    }
  };

  return (
    <>
      <div className="sm:hidden">
        <div onClick={() => setIsComponentVisible(false)} />
        <RootAutocomplete>
          <SearchInput
            className="text-black"
            parantDivClass="bg-grey-200"
            onTextChanged={onTextChanged}
            searchText={searchText}
            isComponentVisible={isComponentVisible}
            closeIconCB={closeIconCB}
            placeholder="Search for an IFSC code here…"
          />

          <AutoComplete
            text={searchText}
            suggestions={searchObj}
            refAutocomplete={refAutocomplete}
            isComponentVisible={isComponentVisible}
            routeToDetails={routeToDetails}
          />
        </RootAutocomplete>
      </div>
      <div className="md:hidden lg:hidden xl:hidden">
        <div
          className="border-grey-800 bg-grey-200 border-sm py-2 rounded-md"
          onClick={() => onOpenModal()}
        >
          <div className="flex border-grey-300  rounded-md  focus:bg-white focus:text-gray-900">
            <span className=" flex items-center pl-2">
              <button type="submit" className="p-1 cursor-default">
                <svg
                  className="cursor-pointer"
                  width="28"
                  height="28"
                  viewBox="0 0 28 28"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M21.5028 21.5016L17.7163 17.7151M19.7619 12.797C19.7619 16.6429 16.6441 19.7607 12.7982 19.7607C8.95223 19.7607 5.83447 16.6429 5.83447 12.797C5.83447 8.95101 8.95223 5.83325 12.7982 5.83325C16.6441 5.83325 19.7619 8.95101 19.7619 12.797Z"
                    stroke="#9092A3"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </span>
            <Input
              id="input"
              autoComplete="off"
              placeholder="Search for an IFSC code here…"
              type="text"
              className="pl-10 bg-transparent text-sm focus:outline-none border-none"
            />
          </div>
        </div>

        <MarktechModal
          open={openFilter}
          onClose={onCloseModal}
          classNames={{
            modal: 'w-full mobile-search-box',
          }}
          closeIcon={<CloseIcon />}
          center
        >
          <MobileSearchContainer className="pt-4 pr-4">
            <div className="flex">
              <div className="back-icon cursor-pointer" onClick={onCloseModal}>
                <BackIcon />
              </div>
              <RootAutocomplete>
                <SearchInput
                  parantDivClass="bg-grey-200"
                  onTextChanged={onTextChanged}
                  searchText={searchText}
                  isComponentVisible={isComponentVisible}
                  closeIconCB={closeIconCB}
                  autofocus={true}
                  placeholder="Search for an IFSC code here…"
                />
                <AutoComplete
                  text={searchText}
                  suggestions={searchObj}
                  refAutocomplete={refAutocomplete}
                  isComponentVisible={isComponentVisible}
                  routeToDetails={routeToDetails}
                />
              </RootAutocomplete>
            </div>
          </MobileSearchContainer>
        </MarktechModal>
      </div>
      <ReCAPTCHA
        ref={recaptchaRef}
        size="invisible"
        sitekey={RECAPTCHA_SITE_KEY.commonLead}
      />
    </>
  );
};

export default SearchIFSC;
